'use client';
import React from 'react';
import { useForm } from 'react-hook-form';
import { ChevronRight } from 'lucide-react';
import { TutorConfig } from './AITutorBuilderContent';

interface Props {
  config: TutorConfig;
  updateConfig: (u: Partial<TutorConfig>) => void;
  onNext: () => void;
}

const personalities = [
  { id: 'encouraging', label: 'Encouraging', desc: 'Warm praise, celebrates every win', emoji: '🌟' },
  { id: 'playful', label: 'Playful', desc: 'Games, jokes, and fun analogies', emoji: '🎮' },
  { id: 'formal', label: 'Formal', desc: 'Precise, structured, reference-rich', emoji: '📚' },
  { id: 'socratic', label: 'Socratic', desc: 'Asks questions to guide discovery', emoji: '🧠' },
];

const tones = [
  { id: 'friendly', label: 'Friendly' },
  { id: 'calm', label: 'Calm' },
  { id: 'energetic', label: 'Energetic' },
  { id: 'professional', label: 'Professional' },
];

const avatars = ['🤖', '🦉', '🌟', '🧑‍🏫', '🦋', '🌱', '🚀', '🔭'];

export default function TutorBuilderStep1({ config, updateConfig, onNext }: Props) {
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: { name: config.name },
  });

  const onSubmit = (data: { name: string }) => {
    updateConfig({ name: data.name });
    onNext();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
      <div>
        <h2 className="text-xl font-bold text-foreground mb-1">Tutor Identity</h2>
        <p className="text-sm text-muted-foreground">Give your AI tutor a name, personality, and teaching style.</p>
      </div>

      {/* Name */}
      <div>
        <label htmlFor="tutor-name" className="block text-sm font-semibold text-foreground mb-1.5">
          Tutor Name
        </label>
        <p className="text-xs text-muted-foreground mb-2">What should your tutor be called? This name will appear in all your lessons.</p>
        <input
          id="tutor-name"
          type="text"
          {...register('name', { required: 'Give your tutor a name' })}
          placeholder="e.g. Jara, Nova, Orion..."
          className="w-full px-4 py-2.5 bg-input border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring transition-all max-w-sm"
        />
        {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>}
      </div>

      {/* Avatar */}
      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">Tutor Avatar</label>
        <p className="text-xs text-muted-foreground mb-3">Choose an icon that represents your tutor's spirit.</p>
        <div className="flex flex-wrap gap-3">
          {avatars.map((av) => (
            <button
              key={`avatar-${av}`}
              type="button"
              onClick={() => updateConfig({ avatar: av })}
              className={`w-12 h-12 text-2xl rounded-xl border-2 transition-all btn-scale ${
                config.avatar === av
                  ? 'border-primary bg-primary/10 shadow-sm'
                  : 'border-border bg-card hover:border-muted-foreground/40'
              }`}
            >
              {av}
            </button>
          ))}
        </div>
      </div>

      {/* Personality */}
      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">Personality Style</label>
        <p className="text-xs text-muted-foreground mb-3">How does your tutor approach teaching?</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {personalities.map((p) => (
            <button
              key={`personality-${p.id}`}
              type="button"
              onClick={() => updateConfig({ personality: p.id })}
              className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all btn-scale ${
                config.personality === p.id
                  ? 'border-primary bg-primary/5' :'border-border bg-card hover:border-muted-foreground/30'
              }`}
            >
              <span className="text-2xl">{p.emoji}</span>
              <div>
                <p className="text-sm font-semibold text-foreground">{p.label}</p>
                <p className="text-xs text-muted-foreground">{p.desc}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Tone */}
      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">Teaching Tone</label>
        <div className="flex flex-wrap gap-2">
          {tones.map((t) => (
            <button
              key={`tone-${t.id}`}
              type="button"
              onClick={() => updateConfig({ tone: t.id })}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all btn-scale ${
                config.tone === t.id
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <button
        type="submit"
        className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-semibold rounded-xl btn-scale hover:bg-primary/90 transition-colors"
      >
        Continue to Curriculum <ChevronRight size={16} />
      </button>
    </form>
  );
}